import { FC, useEffect, useState } from 'react';

import AddressFinder, {
  ADDRESS_FINDER_LOOKUP_ERROR_ID,
  defaultValues as addressFinderDefaultValues,
  addressFinderSchema,
} from '@eneco/components/AddressFinder/AddressFinder';
import { Layout, NextButton, StepHeader } from '@eneco/flows/components';
import { useFlowRedirect } from '@eneco/flows/hooks/useFlowRedirect';
import { useFlowHooks } from '@eneco/flows/utils/FlowProvider';
import { typedYupResolver } from '@eneco/hooks/form';
import { EnergyUsagePurposeSelector } from '@eneco/sitecore-components-content/AddressFinder/EnergyUsagePurpose/EnergyUsagePurposeSelector';
import { Stack } from '@eneco/sparky';
import { Customers_Address_AddressModel } from '@monorepo-types/dc';
import { usePlaceholderContent } from '@sitecore/common';
import { AddressStepRendering } from '@sitecore/types/AddressStep';
import { FieldErrors, FormProvider, UseFormHandleSubmit, UseFormSetFocus, useForm } from 'react-hook-form';
import * as yup from 'yup';

import { FlowConsumerContextEnergy } from '../../../context';
import { useFlowNavigation } from '../../hooks';

interface FormFields {
  postalCode: string;
  houseNumber: number;
  houseNumberSuffix?: string;
  city: string;
  street: string;
  energyUsagePurpose?: string;
  [key: string]: unknown;
}

/**
 * Screen-reader instructions for this step until Sitecore exposes a dedicated field.
 * Covers: what to enter, what we do with it, and how to continue.
 */
const ADDRESS_STEP_SR_INSTRUCTIONS =
  'Vul je postcode en huisnummer in. Heb je een huisnummertoevoeging, kies die dan ook. We zoeken je adres op om een persoonlijk energieaanbod te maken. Controleer het gevonden adres en ga verder met de knop.';

const ADDRESS_GROUP_LABEL_FALLBACK = 'Adres invoer';

const ADDRESS_STEP_ERROR_FIELD_ORDER = [
  'postalCode',
  'houseNumber',
  'houseNumberSuffix',
  'energyUsagePurpose',
] as const;

const focusFirstAddressStepError = (errors: FieldErrors<FormFields>, setFocus: UseFormSetFocus<FormFields>) => {
  const firstField = ADDRESS_STEP_ERROR_FIELD_ORDER.find(fieldName => errors[fieldName]);

  if (firstField) {
    setFocus(firstField);
    document.getElementsByName(firstField)[0]?.focus();
    return;
  }

  document.getElementById(ADDRESS_FINDER_LOOKUP_ERROR_ID)?.focus();
};

export const AddressStep: FC = () => {
  const { useFlowSelector } = useFlowHooks<FlowConsumerContextEnergy>();
  const flowState = useFlowSelector(state => state);
  const { updateContextAndGoToNextStep } = useFlowNavigation();
  const { customRedirect } = useFlowRedirect();

  const [isSuffixRequired, setIsSuffixRequired] = useState(false);

  const {
    context: { postalCode, houseNumber, houseNumberSuffix, street, city, energyUsagePurpose },
  } = flowState;

  const { AddressStep: { fields: textualData } = {} } = usePlaceholderContent<{ AddressStep: AddressStepRendering }>();

  const formSchema = yup.object({
    ...addressFinderSchema(isSuffixRequired).fields,
    energyUsagePurpose: yup.string().when([], {
      is: () =>
        Boolean(
          Array.isArray(textualData?.energyUsage?.energyUsagePurposeList?.value?.options) &&
          (textualData?.energyUsage?.energyUsagePurposeList?.value?.options?.length ?? 0) > 0,
        ),
      then: schema =>
        schema
          .required(textualData?.energyUsage?.energyUsagePurposeList?.value?.requiredMessage)
          .notOneOf(['none', ''], textualData?.energyUsage?.energyUsagePurposeList?.value?.requiredMessage),
      otherwise: schema => schema.notRequired(),
    }),
  });

  const formMethods = useForm<FormFields>({
    mode: 'onBlur',
    defaultValues: {
      ...addressFinderDefaultValues,
      postalCode,
      houseNumber,
      houseNumberSuffix,
      street,
      city,
      energyUsagePurpose,
    },
    resolver: typedYupResolver<FormFields>(formSchema),
  });

  const {
    handleSubmit,
    getValues,
    register,
    control,
    setFocus,
    formState: { isSubmitSuccessful },
  } = formMethods;

  const [addressFinderData, setAddressFinderData] = useState<Customers_Address_AddressModel>();

  const handleSubmitWithErrorFocus: UseFormHandleSubmit<FormFields> = (onValid, onInvalid) =>
    handleSubmit(onValid, (errors, event) => {
      focusFirstAddressStepError(errors, setFocus);
      onInvalid?.(errors, event);
    });

  useEffect(() => {
    if (isSubmitSuccessful) {
      const formData = getValues();

      // TODO: Update redirect link to use Sitecore textualData when available
      if (formData.energyUsagePurpose === 'business') {
        customRedirect('/mkb/bestellen/medewerkers/', {
          queryParams: {
            postalCode: addressFinderData?.postalCode?.toString()?.replace?.(' ', ''),
            houseNumber: addressFinderData?.houseNumber?.toString(),
            houseNumberSuffix: addressFinderData?.houseNumberSuffix,
          },
        });
        return;
      }

      updateContextAndGoToNextStep({
        ...formData,
        postalCode: addressFinderData?.postalCode?.toString() ?? formData.postalCode,
        houseNumber: Number(addressFinderData?.houseNumber ?? formData.houseNumber),
        houseNumberSuffix: addressFinderData?.houseNumberSuffix ?? formData.houseNumberSuffix ?? houseNumberSuffix,
        energyUsagePurpose: formData.energyUsagePurpose,
      } as Partial<FlowConsumerContextEnergy>);
    }
  }, [isSubmitSuccessful]);

  const addressGroupLabel = textualData?.content?.addressGroupLabel?.value ?? ADDRESS_GROUP_LABEL_FALLBACK;

  return (
    <Layout.Content variant="A" handleSubmit={handleSubmitWithErrorFocus}>
      <FormProvider {...formMethods}>
        <StepHeader textualData={textualData} />
        <Stack.Item>
          <AddressFinder
            customLabels={{
              houseNumber: textualData?.address?.houseNumberFormField,
              postalCode: textualData?.address?.postalCodeFormField,
              houseNumberSuffix: textualData?.address?.houseNumberSuffixFormField,
              notFoundErrorNotification: textualData?.notification?.notFoundErrorNotification,
              serverErrorNotification: textualData?.notification?.serverErrorNotification,
            }}
            isSuffixRequired={isSuffixRequired}
            setIsSuffixRequired={setIsSuffixRequired}
            setAddressFinderData={setAddressFinderData}
            addressGroupLabel={`${addressGroupLabel}. ${ADDRESS_STEP_SR_INSTRUCTIONS}`}
          />

          {textualData?.energyUsage?.energyUsagePurposeList && (
            <EnergyUsagePurposeSelector
              control={control}
              energyUsagePurposeList={textualData?.energyUsage?.energyUsagePurposeList}
              content={textualData?.energyUsage?.energyUsagePurposeContent}
              direction="row"
            />
          )}
        </Stack.Item>
        <input {...register('city')} type="hidden" />
        <input {...register('street')} type="hidden" />
        <NextButton isLoading={isSubmitSuccessful}>{textualData?.content?.nextStepText?.value}</NextButton>
      </FormProvider>
    </Layout.Content>
  );
};
